import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import debounce from 'lodash/debounce';

import { analytics } from '../../services/analytics';

import { getHorizontalPosition } from './getGamePosition';

export const SEEN_TIME = 2000;
export const DEBOUNCE_DELAY = 2000;

function getHeaderHeight() {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
  return value.endsWith('px') ? parseFloat(value) : 0;
}

function getFooterNavTop() {
  const footerNav = document.querySelector<HTMLElement>('[data-footer-nav]');
  if (!footerNav) {
    return Infinity;
  }
  const rect = footerNav.getBoundingClientRect();
  // Hidden (e.g. landscape/desktop, or `hideNavigationBar`) elements report a zero-height rect.
  if (rect.height === 0) {
    return Infinity;
  }
  return rect.top + window.scrollY;
}

function getViewPort() {
  const headerHeight = getHeaderHeight();
  return {
    top: document.documentElement.scrollTop + headerHeight,
    bottom: Math.min(document.documentElement.scrollTop + window.innerHeight, getFooterNavTop()),
    left: document.documentElement.scrollLeft,
    right: document.documentElement.scrollLeft + window.innerWidth,
  };
}

export function isElementSeen(element: HTMLElement) {
  const viewPort = getViewPort();
  const rect = element.getBoundingClientRect();
  const top = rect.top + window.scrollY;
  const bottom = rect.bottom + window.scrollY;
  const visibleHeight = Math.min(viewPort.bottom, bottom) - Math.max(viewPort.top, top);
  return rect.height > 0 && (visibleHeight / rect.height) * 100 >= 50;
}

function sendGamesImpression(payload: AnalyticsEvents['games_impression']) {
  if (payload.slot_id.length > 0) {
    void analytics.sendEvent('games_impression', payload);
  }
}

function getRowGames(row: HTMLDivElement) {
  return Array.from(row.querySelectorAll<HTMLDivElement>(`[data-game-id]`));
}

function gridListener() {
  const viewPort = getViewPort();
  const row = document.querySelector<HTMLDivElement>('[data-games-row]');
  if (!row) {
    return;
  }
  const games = getRowGames(row);
  if (!games.length) {
    return;
  }

  const payload: AnalyticsEvents['games_impression'] = {
    slot_id: [],
    category: [],
    category_position: [],
    vertical_location: [],
    horizontal_location: [],
  };

  const category = row.getAttribute('data-games-row')!;
  const categoryPosition = Number(row.getAttribute('data-category-position')) || 0;
  // An auto-scrolling carousel repeats its games to close the loop, so the same game can be on
  // screen more than once. Each one is reported once — see `seen` in `lobbyListener`.
  const seen = new Set<string>();
  let currOffset = row.offsetTop;
  let vertPos = 1;
  let horPos = 1;
  for (let i = 0; i < games.length; i++) {
    const game = games[i];
    if (game.offsetTop > viewPort.bottom) {
      // If the game is below the viewport, we can stop the loop
      break;
    }
    if (currOffset < game.offsetTop) {
      vertPos += 1;
      horPos = 1;
      currOffset = game.offsetTop;
    }
    const top = game.getBoundingClientRect().top + window.scrollY;
    const height = game.getBoundingClientRect().height;
    const visibleHeight = Math.min(viewPort.bottom, top + height) - Math.max(viewPort.top, top);
    if ((visibleHeight / height) * 100 < 50) {
      continue;
    }
    const id = game.getAttribute('data-game-id');
    if (!id) {
      // If the game doesn't have an id, something is wrong, so we should stop the loop
      throw new Error('data-game-id is not set');
    }
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    payload.slot_id.push(id);
    payload.category.push(category);
    payload.category_position.push(categoryPosition);
    payload.vertical_location.push(vertPos);
    payload.horizontal_location.push(getHorizontalPosition(game, horPos - 1));
    horPos += 1;
  }

  sendGamesImpression(payload);
}

function lobbyListener() {
  const viewPort = getViewPort();

  const payload: AnalyticsEvents['games_impression'] = {
    slot_id: [],
    category: [],
    category_position: [],
    vertical_location: [],
    horizontal_location: [],
  };

  const allRows = Array.from(document.querySelectorAll<HTMLDivElement>('[data-games-row]'));

  allRows
    .filter((row) => {
      const top = row.getBoundingClientRect().top + window.scrollY;
      const bottom = row.getBoundingClientRect().bottom + window.scrollY;
      if (top > viewPort.top && bottom < viewPort.bottom) {
        return true;
      }
      const height = bottom - top;
      const visibleHeight = Math.min(viewPort.bottom, bottom) - Math.max(viewPort.top, top);
      return (visibleHeight / height) * 100 > 50;
    })
    .forEach((row) => {
      const rowLeft = row.getBoundingClientRect().left + window.scrollX;
      const rowRight = row.getBoundingClientRect().right + window.scrollX;
      const games = getRowGames(row);
      if (!games.length) {
        return;
      }
      const category = row.getAttribute('data-games-row')!;
      const categoryPosition = allRows.indexOf(row) + 1;
      /**
       * An auto-scrolling carousel (PLT-795) lays the same games out several times over to close
       * its loop, and rests a whole copy in — so the cards on screen are almost always repeats and
       * the originals are parked off to the left. Discarding the repeats therefore reported nothing
       * at all for a carousel row; keeping them all reported the same game once per copy. Whichever
       * instance passes the visibility test first is the one the player can see, so it is the one
       * counted, and the rest of its id is skipped.
       */
      const seen = new Set<string>();
      for (let i = 0; i < games.length; i++) {
        const game = games[i];
        // A short carousel fades its far cards out while they still sit inside the row's bounds.
        const presence = game.closest<HTMLElement>('[data-carousel-item]')?.style.getPropertyValue('--presence');
        if (presence && parseFloat(presence) < 0.5) {
          continue;
        }
        const rect = game.getBoundingClientRect();
        const right = rect.right + window.scrollX;
        // Skip games that are not visible
        if (right < rowLeft) {
          continue;
        }
        // Since games are sorted by horizontal position, we can break the loop here
        const left = rect.left + window.scrollX;
        if (left > rowRight) {
          break;
        }
        const width = rect.width;
        const visibleWidth = Math.min(rowRight, right) - Math.max(rowLeft, left);
        if ((visibleWidth / width) * 100 < 50) {
          continue;
        }
        const top = rect.top + window.scrollY;
        const bottom = rect.bottom + window.scrollY;
        const height = rect.height;
        const visibleHeight = Math.min(viewPort.bottom, bottom) - Math.max(viewPort.top, top);
        if (height === 0 || (visibleHeight / height) * 100 < 50) {
          continue;
        }
        const id = game.getAttribute('data-game-id');
        if (!id) {
          throw new Error('data-game-id is not set');
        }
        if (seen.has(id)) {
          continue;
        }
        seen.add(id);
        payload.slot_id.push(id);
        payload.category.push(category);
        payload.category_position.push(categoryPosition);
        payload.vertical_location.push(categoryPosition);
        payload.horizontal_location.push(getHorizontalPosition(game, i));
      }
    });

  sendGamesImpression(payload);
}

// Only fire after the user has stopped scrolling for DEBOUNCE_DELAY.
export const seenGamesGridListener = debounce(gridListener, DEBOUNCE_DELAY);
export const seenGamesLobbyListener = debounce(lobbyListener, DEBOUNCE_DELAY);
